"use client";

import { Api } from "@/app/api";
import type {
	ChatbotConversation,
	ChatbotKnowledge,
	ChatbotMessage,
	ChatbotPaginatedResponse,
} from "@/types";

export const chatbotInjection = Api.injectEndpoints({
	endpoints: (builder) => ({
		getChatbotConversation: builder.query<ChatbotConversation | null, void>({
			query: () => ({
				url: "/chatbot/conversation",
				method: "GET",
			}),
			providesTags: (result) =>
				result
					? [{ type: "chatbot-conversations", id: result._id }]
					: [{ type: "chatbot-conversations", id: "CURRENT" }],
		}),
		getChatbotMessages: builder.query<
			ChatbotPaginatedResponse<ChatbotMessage>,
			{ page?: number; limit?: number } | void
		>({
			query: (args) => ({
				url: "/chatbot/conversation/messages",
				method: "GET",
				params: {
					page: args?.page ?? 1,
					limit: args?.limit ?? 100,
				},
			}),
			providesTags: [{ type: "chatbot-messages", id: "CURRENT" }],
		}),
		sendChatbotMessage: builder.mutation<
			ChatbotConversation,
			{ message: string; fileUrls?: string[] }
		>({
			query: (body) => ({
				url: "/chatbot/messages",
				method: "POST",
				body,
				headers: { "Content-Type": "application/json" },
			}),
			invalidatesTags: [
				{ type: "chatbot-conversations", id: "CURRENT" },
				{ type: "chatbot-messages", id: "CURRENT" },
				{ type: "chatbot-conversations", id: "ADMIN_LIST" },
			],
		}),
		markChatbotConversationViewed: builder.mutation<{ success: boolean }, void>({
			query: () => ({
				url: "/chatbot/conversation/viewed",
				method: "PATCH",
				headers: { "Content-Type": "application/json" },
			}),
			invalidatesTags: [
				{ type: "chatbot-conversations", id: "CURRENT" },
			],
		}),
		getAdminChatbotConversations: builder.query<
			ChatbotPaginatedResponse<ChatbotConversation>,
			{ page?: number; limit?: number } | void
		>({
			query: (args) => ({
				url: "/chatbot/admin/conversations",
				method: "GET",
				params: {
					page: args?.page ?? 1,
					limit: args?.limit ?? 20,
					sortBy: "lastMessageAt:desc",
				},
			}),
			providesTags: (result) =>
				result
					? [
							...result.docs.map((conversation) => ({
								type: "chatbot-conversations" as const,
								id: conversation._id,
							})),
							{ type: "chatbot-conversations" as const, id: "ADMIN_LIST" },
						]
					: [{ type: "chatbot-conversations" as const, id: "ADMIN_LIST" }],
		}),
		getAdminChatbotConversation: builder.query<ChatbotConversation, string>({
			query: (id) => ({
				url: `/chatbot/admin/conversations/${id}`,
				method: "GET",
			}),
			providesTags: (_, __, id) => [{ type: "chatbot-conversations", id }],
		}),
		getAdminChatbotMessages: builder.query<
			ChatbotPaginatedResponse<ChatbotMessage>,
			{ conversationId: string; page?: number; limit?: number }
		>({
			query: ({ conversationId, page = 1, limit = 100 }) => ({
				url: `/chatbot/admin/conversations/${conversationId}/messages`,
				method: "GET",
				params: { page, limit },
			}),
			providesTags: (_, __, { conversationId }) => [
				{ type: "chatbot-messages", id: conversationId },
			],
		}),
		sendAdminChatbotMessage: builder.mutation<
			ChatbotConversation,
			{ conversationId: string; message: string; fileUrls?: string[] }
		>({
			query: ({ conversationId, ...body }) => ({
				url: `/chatbot/admin/conversations/${conversationId}/messages`,
				method: "POST",
				body,
				headers: { "Content-Type": "application/json" },
			}),
			invalidatesTags: (_, __, { conversationId }) => [
				{ type: "chatbot-conversations", id: conversationId },
				{ type: "chatbot-conversations", id: "ADMIN_LIST" },
				{ type: "chatbot-messages", id: conversationId },
			],
		}),
		getAdminChatbotKnowledge: builder.query<
			ChatbotPaginatedResponse<ChatbotKnowledge>,
			{ page?: number; limit?: number } | void
		>({
			query: (args) => ({
				url: "/chatbot/admin/knowledge",
				method: "GET",
				params: {
					page: args?.page ?? 1,
					limit: args?.limit ?? 20,
				},
			}),
			providesTags: [{ type: "chatbot-knowledge", id: "LIST" }],
		}),
		createAdminChatbotKnowledge: builder.mutation<
			ChatbotKnowledge,
			{ title: string; content: string; tags?: string[]; entryType?: "KNOWLEDGE" | "RULE"; isActive?: boolean }
		>({
			query: (body) => ({
				url: "/chatbot/admin/knowledge",
				method: "POST",
				body,
				headers: { "Content-Type": "application/json" },
			}),
			invalidatesTags: [{ type: "chatbot-knowledge", id: "LIST" }],
		}),
		updateAdminChatbotKnowledge: builder.mutation<
			ChatbotKnowledge,
			{ id: string; title: string; content: string; tags?: string[]; entryType?: "KNOWLEDGE" | "RULE"; isActive?: boolean }
		>({
			query: ({ id, ...body }) => ({
				url: `/chatbot/admin/knowledge/${id}`,
				method: "PATCH",
				body,
				headers: { "Content-Type": "application/json" },
			}),
			invalidatesTags: [{ type: "chatbot-knowledge", id: "LIST" }],
		}),
		deleteAdminChatbotKnowledge: builder.mutation<
			{ success: boolean },
			string
		>({
			query: (id) => ({
				url: `/chatbot/admin/knowledge/${id}`,
				method: "DELETE",
				headers: { "Content-Type": "application/json" },
			}),
			invalidatesTags: [{ type: "chatbot-knowledge", id: "LIST" }],
		}),
	}),
});

export const {
	useGetChatbotConversationQuery,
	useGetChatbotMessagesQuery,
	useSendChatbotMessageMutation,
	useMarkChatbotConversationViewedMutation,
	useGetAdminChatbotConversationsQuery,
	useGetAdminChatbotConversationQuery,
	useGetAdminChatbotMessagesQuery,
	useSendAdminChatbotMessageMutation,
	useGetAdminChatbotKnowledgeQuery,
	useCreateAdminChatbotKnowledgeMutation,
	useUpdateAdminChatbotKnowledgeMutation,
	useDeleteAdminChatbotKnowledgeMutation,
} = chatbotInjection;
